import { Badge, Box, Separator, Text } from "@chakra-ui/react-v3";
import { buildDayjs } from "@metriport/shared/common/date";
import { ResourceAnalysis } from "../components/resource-analysis";
import { ResourceTimelineLayout } from "../components/resource-timeline-layout";
import { SidePanelLayout } from "../components/side-panel-sections/side-panel-layout";
import { MedicationRowData } from "../tables/medication";
import { MedicationTimelineItem } from "./medication-timeline-item";

export type MedicationSidePanelContentProps = {
  data: MedicationRowData;
};

export function MedicationSidePanelContent({ data }: MedicationSidePanelContentProps) {
  // Extract events from the medication series
  const events =
    "events" in data.originalData && Array.isArray(data.originalData.events)
      ? data.originalData.events
      : [];

  // Sort events by date (newest first)
  const sortedEvents = [...events].sort((a, b) => {
    return buildDayjs(b.date).diff(buildDayjs(a.date));
  });

  // Get latest status from most recent event
  const latestEvent = sortedEvents[0];
  const latestStatus = latestEvent
    ? (latestEvent.resource as { status?: string }).status
    : undefined;

  return (
    <SidePanelLayout
      title="Medication"
      subtitle={data.medication}
      metadata={`Last Fill: ${data.lastFillDate}`}
    >
      <ResourceAnalysis />

      <Separator borderColor="border" />

      {/* Medication Details Card */}
      <Box
        borderWidth="1px"
        borderColor="border.emphasized"
        borderRadius="md"
        bg="bg.subtle"
        overflow="hidden"
      >
        <Box display="grid" gridTemplateColumns="120px 1fr" alignItems="center" gap={0}>
          <Text
            fontSize="sm"
            fontWeight="bold"
            py={2}
            px={3}
            borderRight="1px solid"
            borderBottom="1px solid"
            borderColor="border"
            bg="bg.panel"
            color="fg.emphasized"
          >
            Name
          </Text>
          <Text fontSize="sm" py={2} px={3} borderBottom="1px solid" borderColor="border">
            {data.medication}
          </Text>

          <Text
            fontSize="sm"
            fontWeight="bold"
            py={2}
            px={3}
            borderRight="1px solid"
            borderBottom="1px solid"
            borderColor="border"
            bg="bg.panel"
            color="fg.emphasized"
          >
            Status
          </Text>
          <Box py={2} px={3} borderBottom="1px solid" borderColor="border">
            {latestStatus ? (
              <Badge size="sm" variant="subtle" colorPalette={getStatusColor(latestStatus)}>
                {latestStatus}
              </Badge>
            ) : (
              <Text fontSize="sm" color="fg.muted">
                -
              </Text>
            )}
          </Box>

          <Text
            fontSize="sm"
            fontWeight="bold"
            py={2}
            px={3}
            borderRight="1px solid"
            borderBottom="1px solid"
            borderColor="border"
            bg="bg.panel"
            color="fg.emphasized"
          >
            Dosage
          </Text>
          <Text fontSize="sm" py={2} px={3} borderBottom="1px solid" borderColor="border">
            {data.dose || "-"}
          </Text>

          <Text
            fontSize="sm"
            fontWeight="bold"
            py={2}
            px={3}
            borderRight="1px solid"
            borderColor="border"
            bg="bg.panel"
            color="fg.emphasized"
          >
            Quantity
          </Text>
          <Text fontSize="sm" py={2} px={3}>
            {data.quantity || "-"}
          </Text>
        </Box>
      </Box>

      <Separator borderColor="border" />

      <ResourceTimelineLayout
        title={`Events (${sortedEvents.length})`}
        items={sortedEvents.map((event, index) => ({
          date: event.date,
          content: (
            <MedicationTimelineItem key={`${event.type}-${index}`} resource={event.resource} />
          ),
        }))}
      />
    </SidePanelLayout>
  );
}

function getStatusColor(status: string): string {
  switch (status) {
    case "on-hold":
    case "triaged":
      return "yellow";
    case "in-progress":
    case "active":
      return "teal";
    case "completed":
      return "green";
    case "cancelled":
    case "entered-in-error":
    case "stopped":
      return "red";
    default:
      return "gray";
  }
}
